/**
 * Variant image swap for the "grid" gallery layout on the product page.
 *
 * The default Dawn-derived flow only updates the slider/thumbnail viewer when a
 * variant changes. In grid layout the desktop main image is rendered as a
 * static `<div class="product-media-grid__main" data-main-image>` outside any
 * `GalleryViewer-*` element, so it never updates.
 *
 * This module:
 *   - listens for native `<variant-radios>` / `<variant-selects>` change events
 *   - listens for `?variant=` URL changes (covers BCPO / Best Custom Product
 *     Options and any other app that only updates the URL + hidden id input)
 *   - resolves the variant's `featured_media` from the variants JSON already
 *     printed inside the variant picker (or `#VariantATCData-{sectionId}`)
 *   - rewrites the `<img>` inside `[data-main-image]` and scrolls the mobile
 *     carousel slide that matches the new media into view
 *
 * Scoped per section via `data-section-id` on the script tag.
 */

// Block scrollIntoView on product gallery slides for viewports < 768px.
// Third-party variant pickers (e.g. BCPO / Best Custom Product Options) call
// `scrollIntoView` on the active media slide after a variant change, which
// jumps the page up to the gallery on mobile. We can't edit those scripts,
// so we override `scrollIntoView` only on the slide elements themselves
// (no global prototype mutation, no per-call selector matching).
;(function () {
  if (window.__galleryScrollIntoViewPatched) return
  window.__galleryScrollIntoViewPatched = true

  var mobileMQ = window.matchMedia('(max-width: 767px)')
  var nativeScrollIntoView = Element.prototype.scrollIntoView
  var SELECTOR = '[data-media-id], .product-media-grid__slide, .product__media-item'

  function guardedScrollIntoView() {
    if (mobileMQ.matches) return
    return nativeScrollIntoView.apply(this, arguments)
  }

  function patchSlides(root) {
    var slides = (root || document).querySelectorAll(SELECTOR)
    for (var i = 0; i < slides.length; i++) {
      var el = slides[i]
      if (el.__galleryScrollPatched) continue
      el.__galleryScrollPatched = true
      el.scrollIntoView = guardedScrollIntoView
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { patchSlides() })
  } else {
    patchSlides()
  }

  document.addEventListener('shopify:section:load', function (e) {
    patchSlides(e.target)
  })
})()

;(function () {
  var scriptTag = document.currentScript
  if (!scriptTag) {
    var candidates = document.querySelectorAll('script[src*="variant-image-grid.js"][data-section-id]')
    scriptTag = candidates[candidates.length - 1]
  }
  if (!scriptTag) return

  var sectionId = scriptTag.getAttribute('data-section-id')
  if (!sectionId) return

  var IMAGE_WIDTHS = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946]
  var DEFAULT_WIDTH = 1946

  var variantData = null
  var lastAppliedVariantId = null
  var lastSyncedURL = ''
  var formInputObserver = null

  function getVariantData() {
    if (variantData && variantData.length) return variantData
    try {
      var picker =
        document.querySelector('variant-radios[data-section="' + sectionId + '"] [type="application/json"]') ||
        document.querySelector('variant-selects[data-section="' + sectionId + '"] [type="application/json"]') ||
        document.getElementById('VariantATCData-' + sectionId)
      if (picker) variantData = JSON.parse(picker.textContent)
    } catch (e) {
      variantData = null
    }
    return variantData || []
  }

  function findVariantById(id) {
    if (!id) return null
    var parsed = parseInt(id, 10)
    if (isNaN(parsed)) return null
    var variants = getVariantData()
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === parsed) return variants[i]
    }
    return null
  }

  function getCurrentVariantId() {
    try {
      var params = new URLSearchParams(window.location.search)
      var fromUrl = params.get('variant')
      if (fromUrl) return fromUrl
    } catch (e) {}

    var hidden = document.querySelector(
      '#product-form-' + sectionId + ' input[name="id"]'
    )
    if (hidden && hidden.value) return hidden.value

    return null
  }

  function buildSrcWithWidth(baseSrc, width) {
    if (!baseSrc) return ''
    var separator = baseSrc.indexOf('?') === -1 ? '?' : '&'
    return baseSrc + separator + 'width=' + width
  }

  function buildSrcset(baseSrc, maxWidth) {
    var parts = []
    for (var i = 0; i < IMAGE_WIDTHS.length; i++) {
      var w = IMAGE_WIDTHS[i]
      if (maxWidth && w > maxWidth) continue
      parts.push(buildSrcWithWidth(baseSrc, w) + ' ' + w + 'w')
    }
    return parts.join(', ')
  }

  function getMainImageContainer() {
    var gallery = document.getElementById('MediaGallery-' + sectionId)
    if (!gallery) return null
    return gallery.querySelector('[data-main-image]')
  }

  function cacheOriginal(mainContainer, img) {
    if (mainContainer.dataset.originalSrc) return
    mainContainer.dataset.originalSrc = img.currentSrc || img.src || ''
    mainContainer.dataset.originalSrcset = img.srcset || ''
    mainContainer.dataset.originalSizes = img.sizes || ''
    mainContainer.dataset.originalAlt = img.getAttribute('alt') || ''
  }

  function updateDesktopMainImage(variant) {
    var mainContainer = getMainImageContainer()
    if (!mainContainer) return

    var img = mainContainer.querySelector('.product__media img')
    if (!img) img = mainContainer.querySelector('img')
    if (!img) return

    cacheOriginal(mainContainer, img)

    var media = variant && variant.featured_media
    if (!media) return

    // Only swap for image-type media. Video / 3D model media live in deferred
    // wrappers and need the full media-gallery flow, which is out of scope.
    if (media.media_type && media.media_type !== 'image') return

    var preview = media.preview_image || {}
    var baseSrc = preview.src || (variant.featured_image && variant.featured_image.src)
    if (!baseSrc) return

    // Shopify CDN paths can be returned protocol-relative.
    if (baseSrc.indexOf('//') === 0) {
      baseSrc = window.location.protocol + baseSrc
    }

    var maxWidth = preview.width || null
    var newSrc = buildSrcWithWidth(baseSrc, DEFAULT_WIDTH)
    var newSrcset = buildSrcset(baseSrc, maxWidth)

    if (img.src !== newSrc) img.src = newSrc
    if (img.srcset !== newSrcset) img.srcset = newSrcset
    if (media.alt) img.setAttribute('alt', media.alt)
  }

  function updateMobileCarousel(variant) {
    var media = variant && variant.featured_media
    if (!media) return

    var viewer = document.getElementById('GalleryViewer-Grid-Mobile-' + sectionId)
    if (!viewer) return

    var targetId = sectionId + '-' + media.id
    var slides = viewer.querySelectorAll('[data-media-id]')
    var match = null

    slides.forEach(function (slide) {
      var isMatch = slide.getAttribute('data-media-id') === targetId
      slide.classList.toggle('is-active', isMatch)
      if (isMatch) match = slide
    })

    if (!match) return

    try {
      var parent = match.parentElement
      if (parent && typeof parent.scrollTo === 'function') {
        parent.scrollTo({ left: match.offsetLeft, behavior: 'smooth' })
      }
    } catch (e) {}
  }

  function applyVariant(variant) {
    if (!variant) return
    if (variant.id === lastAppliedVariantId) return
    lastAppliedVariantId = variant.id

    updateDesktopMainImage(variant)
    updateMobileCarousel(variant)
  }

  function syncFromCurrentState() {
    var variant = findVariantById(getCurrentVariantId())
    if (variant) applyVariant(variant)
  }

  function onURLChange() {
    var currentURL = window.location.href
    if (currentURL === lastSyncedURL) return
    lastSyncedURL = currentURL

    syncFromCurrentState()
    setTimeout(syncFromCurrentState, 100)
    setTimeout(syncFromCurrentState, 350)
  }

  function onVariantPickerChange() {
    // Defer one tick so VariantSelects.updateMasterId() has run and the URL has
    // been updated before we read the current state.
    setTimeout(syncFromCurrentState, 0)
  }

  function attachVariantPickerListeners() {
    var pickers = document.querySelectorAll(
      'variant-radios[data-section="' + sectionId + '"], ' +
      'variant-selects[data-section="' + sectionId + '"]'
    )
    pickers.forEach(function (picker) {
      picker.addEventListener('change', onVariantPickerChange)
    })
  }

  function attachHiddenInputListener() {
    var hidden = document.querySelector(
      '#product-form-' + sectionId + ' input[name="id"]'
    )
    if (!hidden) return

    hidden.addEventListener('change', onVariantPickerChange)

    if (formInputObserver) formInputObserver.disconnect()
    try {
      formInputObserver = new MutationObserver(function () {
        onVariantPickerChange()
      })
      formInputObserver.observe(hidden, { attributes: true, attributeFilter: ['value'] })
    } catch (e) {}
  }

  function patchHistory() {
    try {
      var origPushState = history.pushState
      history.pushState = function () {
        origPushState.apply(this, arguments)
        onURLChange()
      }
    } catch (e) {}

    try {
      var origReplaceState = history.replaceState
      history.replaceState = function () {
        origReplaceState.apply(this, arguments)
        onURLChange()
      }
    } catch (e) {}

    window.addEventListener('popstate', onURLChange)
  }

  function init() {
    if (!getMainImageContainer()) return

    attachVariantPickerListeners()
    attachHiddenInputListener()
    patchHistory()

    lastSyncedURL = window.location.href
    syncFromCurrentState()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
